import React, { useState, useContext, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import Card from "../../../../components/Card/Card.js";
import CardBody from "../../../../components/Card/CardBody.js";
import {
  Box,
  makeStyles,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from "@material-ui/core";
import { grayColor } from "assets/jss/material-dashboard-pro-react.js";
import AuthContext from "../../../../connections/auth/AuthProvider";
import { useTranslation } from "react-i18next";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import ComparativeTableView from "./ComparativeTable/index.js";
import { getComparisonFilterOptions } from "../../viewServices/calls/apiCalls";
import { FilterSystem } from "../../sharedComponents/FilterSystem/index.js";

const comparativeTabStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  containerBox: {
    padding: theme.spacing(0),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    height: "100%",
    overflow: "hidden",
  },
  controlCard: {
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    padding: theme.spacing(0),
  },
  controlCardBody: {
    padding: 0,
  },
  controlCardContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flex: "1",
    maxWidth: "100%",
    overflow: "visible",
  },
  pnlFlagBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    marginLeft: theme.spacing(2),
    flex: "0 0 auto",
  },
  divider: {
    width: "1px",
    backgroundColor: grayColor[4],
    alignSelf: "stretch",
    margin: `${theme.spacing(1)}px 0`,
  },
  filtersBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flex: "1",
    maxWidth: "900px",
    overflow: "visible",
  },
  selectFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 140,
    maxWidth: 200,
    margin: "7px 0 7px 0 !important",
    "& .MuiInputLabel-root": {
      "&.Mui-focused": {
        color: clientColor + "!important",
      },
    },
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  filterFormControl: {
    marginBottom: theme.spacing(1),
    minWidth: 130,
    maxWidth: 150,
    margin: "7px 0 7px 0 !important",
    "& > div": {
      "&:before": {
        borderBottomWidth: "1px !important",
        borderBottomColor: grayColor[4] + "!important",
      },
      "&:after": {
        borderBottomColor: clientColor + "!important",
      },
    },
  },
  clearFiltersButton: {
    marginLeft: theme.spacing(1),
    minHeight: "40px",
    minWidth: "40px",
    borderColor: grayColor[4],
    color: grayColor[1],
    "&:hover": {
      backgroundColor: grayColor[11],
      color: grayColor[1],
    },
  },
});

ComparativeTab.displayName = "ComparativeTab";

export default function ComparativeTab({ scenarios = [] }) {
  const { userDetails } = useContext(AuthContext);
  const clientColor = userDetails.implementation_color;

  const useStyles = makeStyles((theme) =>
    comparativeTabStyle(theme, clientColor),
  );
  const classes = useStyles();

  const T_PREFIX = "rgm.cockpit";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Comparison selector state
  const [selectedComparison, setSelectedComparison] = useState(null);

  // Filter state management - STANDARD 5 dimensions only
  const [selectedFilters, setSelectedFilters] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });

  // Filter options state - STANDARD 5 dimensions only
  const [filterOptions, setFilterOptions] = useState({
    products: [],
    brands: [],
    channels: [],
    regions: [],
    clients: [],
  });

  // Special parameter - PNL routes filter (separate from FilterSystem)
  const [selectedPnlFlag, setSelectedPnlFlag] = useState("");
  const [pnlFlagOptions, setPnlFlagOptions] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  // Filter dimensions configuration - STANDARD 5 only
  const filterDimensions = [
    { key: "products", label: t("overview.filter_dimensions.products") },
    { key: "brands", label: t("overview.filter_dimensions.brands") },
    { key: "channels", label: t("overview.filter_dimensions.channels") },
    { key: "regions", label: t("overview.filter_dimensions.regions") },
    { key: "clients", label: t("overview.filter_dimensions.clients") },
  ];

  // Reset state when component mounts (do NOT preserve state for Comparative tab)
  useEffect(() => {
    // Always start fresh - reset comparison and filters
    setSelectedComparison(null);
    setSelectedFilters({
      products: [],
      brands: [],
      channels: [],
      regions: [],
      clients: [],
    });
    setSelectedPnlFlag("");
  }, []); // Run only once on mount

  // Fetch filter options when comparison changes
  useEffect(() => {
    const fetchFilterOptions = async () => {
      if (!selectedComparison) {
        setFilterOptions({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setPnlFlagOptions([]);
        // Reset filters when comparison changes
        setSelectedFilters({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setSelectedPnlFlag("");
        return;
      }

      setIsLoadingFilters(true);
      try {
        const response = await getComparisonFilterOptions(
          selectedComparison.comparison_id,
        );
        if (response.status === "success" && response.data) {
          const dimensionData = response.data;
          setFilterOptions({
            products: dimensionData.product?.values || [],
            brands: dimensionData.brand?.values || [],
            channels: [], // Not available in comparisons
            regions: dimensionData.region?.values || [],
            clients: dimensionData.client?.values || [],
          });
          // Set PNL flag options (special parameter)
          setPnlFlagOptions(dimensionData.pnl_flags || []);
        }
      } catch (error) {
        console.error("Failed to fetch filter options:", error);
        setFilterOptions({
          products: [],
          brands: [],
          channels: [],
          regions: [],
          clients: [],
        });
        setPnlFlagOptions([]);
      } finally {
        setIsLoadingFilters(false);
      }
    };

    fetchFilterOptions();
  }, [selectedComparison]);

  // Unified filter change handler
  const handleFiltersChange = useCallback((newFilters) => {
    setSelectedFilters(newFilters);
  }, []);

  return (
    <Box className={classes.containerBox}>
      {/* Control Card with Comparison Selector and Filters */}
      <Card className={classes.controlCard}>
        <CardBody className={classes.controlCardBody}>
          <Box className={classes.controlCardContent}>
            {/* Left Section: PNL Routes Filter + Filters */}
            <Box className={classes.leftSection}>
              {/* PNL Routes Filter Selector (Special parameter) */}
              <Box className={classes.pnlFlagBox}>
                <FormControl
                  className={classes.selectFormControl}
                  style={{ minWidth: 140, marginBottom: 0 }}
                >
                  <InputLabel id="pnl-flag-selector-label" shrink>
                    {t("overview.considered_routes_label")}
                  </InputLabel>
                  <Select
                    labelId="pnl-flag-selector-label"
                    value={selectedPnlFlag}
                    onChange={(e) => setSelectedPnlFlag(e.target.value)}
                    label={t("overview.considered_routes_label")}
                    displayEmpty
                    disabled={!selectedComparison || isLoadingFilters}
                    renderValue={(selected) => {
                      if (!selected || selected === "") {
                        return <em>{t("pnl.all_routes")}</em>;
                      }
                      return selected;
                    }}
                    MenuProps={{
                      className: classes.selectMenu,
                      classes: { paper: classes.selectPaper },
                    }}
                  >
                    <MenuItem value="" className={classes.selectMenuItem}>
                      <em>{t("pnl.all_routes")}</em>
                    </MenuItem>
                    {pnlFlagOptions.map((option) => (
                      <MenuItem
                        key={option}
                        value={option}
                        className={classes.selectMenuItem}
                      >
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Divider */}
              <Box className={classes.divider} />

              {/* FilterSystem Component - Standard 5 dimensions */}
              <Box className={classes.filtersBox}>
                <FilterSystem
                  dimensions={filterDimensions}
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  onFiltersChange={handleFiltersChange}
                  clientColor={clientColor}
                  disabled={!selectedComparison || isLoadingFilters}
                />
              </Box>
            </Box>
          </Box>
        </CardBody>
      </Card>

      {/* Price Route Display Area */}
      <Card
        style={{
          marginTop: 0,
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CardBody
          style={{
            padding: "12px",
            flex: 1,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ComparativeTableView
            selectedComparison={selectedComparison}
            onComparisonSelect={setSelectedComparison}
            selectedFilters={{
              ...selectedFilters,
              pnl_flag: selectedPnlFlag, // Add PNL flag to filters
            }}
            scenarios={scenarios}
          />
        </CardBody>
      </Card>
    </Box>
  );
}

ComparativeTab.propTypes = {
  scenarios: PropTypes.array,
};
